/**
 * @fileoverview Analysis panel: flow chart, per-room data table, energy stats.
 *
 * @module HydraulicBalancingTesting/components/AnalysisPanel
 */

import React from 'react';
import { Table, Tag, Alert } from 'antd';

import { toLitersPerHour } from '../physics';
import { PIPES, SCORING } from '../constants';
import { FlowBarChart } from './Charts';
import { joulesToKWh } from '../hooks';

import styles from '../HydraulicBalancingTesting.module.css';

export const AnalysisPanel = ({ engine, showTargets }) => {
  const { rooms, derived, energy } = engine;
  const { deviations, network } = derived;
  const idxOf = (r) => r.id - 1;

  const columns = [
    { title: 'Room', dataIndex: 'name', key: 'name', render: (v, r) => <span>R{r.id} {v}</span> },
    {
      title: 'Flow [l/h]',
      key: 'flow',
      align: 'right',
      render: (_, r) => toLitersPerHour(network.roomFlows[idxOf(r)]).toFixed(0),
    },
    ...(showTargets
      ? [
          {
            title: 'Design [l/h]',
            key: 'design',
            align: 'right',
            render: (_, r) => toLitersPerHour(r.designFlow).toFixed(0),
          },
          {
            title: 'Dev.',
            key: 'dev',
            align: 'center',
            render: (_, r, i) => {
              const d = deviations[i];
              const ok = Math.abs(d) <= SCORING.BALANCE_TOLERANCE;
              return (
                <Tag color={ok ? 'green' : d > 0 ? 'volcano' : 'blue'} style={{ margin: 0 }}>
                  {d >= 0 ? '+' : ''}{(d * 100).toFixed(0)}%
                </Tag>
              );
            },
          },
        ]
      : [
          {
            title: 'Load [W]',
            key: 'load',
            align: 'right',
            render: (_, r) => r.designLoad.toFixed(0),
          },
        ]),
    {
      title: 'T [°C]',
      key: 't',
      align: 'right',
      render: (_, r) => {
        const err = r.T - r.Tset;
        const color = Math.abs(err) <= 0.5 ? undefined : err > 0 ? '#ff4d4f' : '#1890ff';
        return <span style={{ color }}>{r.T.toFixed(1)}</span>;
      },
    },
    {
      title: 'Return [°C]',
      key: 'ret',
      align: 'right',
      render: (_, r) => r.Treturn.toFixed(1),
    },
    {
      title: 'Auth.',
      key: 'auth',
      align: 'right',
      render: (_, r) => {
        const a = network.authority[idxOf(r)];
        const low = a > 0 && a < SCORING.MIN_AUTHORITY;
        return <span style={low ? { color: '#faad14' } : undefined}>{(a * 100).toFixed(0)}%</span>;
      },
    },
    {
      title: 'v [m/s]',
      key: 'v',
      align: 'right',
      render: (_, r) => {
        const v = network.velocity[idxOf(r)];
        return (
          <span style={v > PIPES.NOISE_VELOCITY ? { color: '#ff4d4f' } : undefined}>
            {v.toFixed(2)}
          </span>
        );
      },
    },
  ];

  return (
    <div className={styles.panelCard}>
      <p className={styles.panelTitle}>Flow distribution</p>
      <FlowBarChart rooms={rooms} deviations={deviations} roomFlows={network.roomFlows} showTargets={showTargets} />

      <p className={styles.panelTitle} style={{ marginTop: 12 }}>Room data</p>
      <Table
        size="small"
        rowKey="id"
        pagination={false}
        columns={columns}
        dataSource={rooms}
      />

      <Alert
        style={{ marginTop: 12 }}
        type="info"
        message={
          <span>
            Energy since scenario start: <b>{joulesToKWh(energy.thermalJ)} kWh</b> heat from{' '}
            <b>{joulesToKWh(energy.electricJ)} kWh</b> electricity → SPF{' '}
            <b>{derived.spf > 0 ? derived.spf.toFixed(2) : '—'}</b>
          </span>
        }
      />
    </div>
  );
};
